<template>
  <HeadTitle
    title="Crear grupo de calendario"
    description="Fomulario de creación de un grupo de calendario"
  />
  <br />
  <Step :step-number="1" title="Alcance del grupo de calendario">
    <div class="flex flex-column">
      <MultiSelectDialog
        header-title="Seleccionar nóminas"
        label="Seleccionar nóminas"
        @update:visible="updateDialogVisibility"
        :options="payrolls"
        v-model="selectedPayrolls"
        :disabled="isDisabled"
        show-key="name"
        value-key="id"
        :required="true"
      />
      <small id="text-error" class="p-error">{{
        payrollsError || '&nbsp;'
      }}</small>
    </div>
    <div class="ml-3 flex flex-column">
      <MultiSelectDialog
        header-title="Seleccionar días hábiles"
        label="Seleccionar días hábiles"
        @update:visible="updateDialogVisibility"
        :options="workingDays"
        v-model="selectedWorkingDays"
        :disabled="isDisabled"
        show-key="name"
        value-key="id"
        :required="true"
      />
      <small id="text-error" class="p-error">{{
        workingDaysError || '&nbsp;'
      }}</small>
    </div>
  </Step>
  <Divider></Divider>
  <Step :step-number="2" title="Lista de feriados">
    <DataTable
      v-model:editingRows="editingRows"
      :value="holidaySteps.data"
      editMode="row"
      dataKey="stepNumber"
      @row-edit-save="onRowEditSave"
      tableClass="editable-cells-table"
      tableStyle="min-width: 30rem"
      scrollHeight="500px"
      scrollable
      class="tabla lg:w-7 md:w-8 sm:w-12"
    >
      <Column header="#" style="width: 5%">
        <template #body="{ data, index }">
          <Chip
            class="flex justify-content-center w-5 h-2rem"
            :class="{ 'bg-teal-200': data.file !== '' }"
            :label="String(index + 1)"
          />
        </template>
      </Column>
      <Column field="name" header="Nombre" style="width: 50%">
        <template #body="{ data, field }">
          {{ data[field] }}
        </template>
        <template #editor="{ data, field }">
          <InputText v-model="data[field]"></InputText>
        </template>
      </Column>
      <column field="date" header="Fecha" style="width: 50%">
        <template #body="{ data, field }">
          <span v-if="data[field]">
            {{ dayjs(data[field]).format('DD/MM/YYYY') }}
          </span>
        </template>
        <template #editor="{ data, field }">
          <Calendar
            v-model="data[field]"
            placeholder="dd/mmm/yyyy"
            showIcon
            showButtonBar
            dateFormat="dd/mm/yy"
            locale="Es"
          ></Calendar>
        </template>
      </column>
      <Column
        :rowEditor="true"
        style="width: 10%; min-width: 8rem"
        bodyStyle="text-align:center"
      ></Column>
      <Column>
        <template #body="{ data, index }">
          <div class="actions">
            <Button
              class="action-btn view p-button-sm"
              icon="pi pi-trash"
              @click="deleteHolidayStep($event, data)"
              severity="danger"
              v-tooltip.bottom="'Eliminar'"
              :disabled="holidaySteps.data.length === index + 1"
            />
          </div>
        </template>
      </Column>
    </DataTable>
  </Step>
  <Divider></Divider>
  <Step :step-number="3" title="Descripción">
    <div class="flex flex-column md:w-4 h-5">
      <Textarea class="mt-2" style="min-width: 250px" v-model="description" />
      <small id="text-error" class="p-error">{{
        descriptionError || '&nbsp;'
      }}</small>
    </div>
  </Step>
  <Footer>
    <Button
      label="Volver"
      class="mr-2"
      severity="secondary"
      @click="
        router.push('/payrollsConfig/' + payrollConfigId + '/calendarGroups')
      "
    />
    <Button
      label="Guardar"
      class="font-normal"
      severity="primary"
      @click="onSave"
      :disabled="!isValidToSave"
    />
  </Footer>
</template>
<script setup lang="ts">
  import { useField } from 'vee-validate'
  import * as yup from 'yup'
  import router from '@/router'
  import useLoading from '@/store/load'
  import { useRoute } from 'vue-router'
  import { computed, onMounted, reactive, ref } from 'vue'
  import useNotifications from '@/store/notification'
  import { getPayrollsByConfigId } from '@/modules/payrolls-config/services/PayrollService'
  import dayjs from 'dayjs'
  import Footer from '@/shared/components/Footer.vue'
  import Step from '@/shared/components/Step.vue'
  import MultiSelectDialog from '@/shared/components/MultiSelectDialog.vue'
  import {
    createCalendarGroup,
    getWorkingDays,
  } from '../services/CalendarGroupService'
  import { Holiday } from '@/modules/employees/models/Holiday'
  import CreateCalendarGroup from '../services/dtos/CreateCalendarGroupDto'
  import HeadTitle from '@/shared/components/HeadTitle.vue'

  const path = useRoute()
  const payrollConfigId = ref()
  const notificationStore = useNotifications()
  const loadingStore = useLoading()

  const payrolls = ref()
  const workingDays = ref()
  const dialogVisible = ref(false)
  const isDisabled = ref(false)

  const holidaySteps = reactive({
    data: [
      {
        stepNumber: 1,
        date: '',
        name: '',
      },
    ] as Holiday[],
    totalItems: 1,
  })

  onMounted(async () => {
    payrollConfigId.value = path.params.id
    const payrollReponse = await getPayrollsByConfigId(
      String(payrollConfigId.value)
    )
    payrolls.value = payrollReponse.map((payroll) => ({
      id: payroll.id,
      name: payroll.code + '-' + payroll.name,
    }))
    const workingDaysResponse = await getWorkingDays()
    workingDays.value = workingDaysResponse.map((workingDay) => ({
      id: workingDay.id,
      name: workingDay.name,
    }))
  })

  const updateDialogVisibility = (value: boolean) => {
    dialogVisible.value = value
  }

  const isValidToSave = computed(() => {
    return (
      holidaySteps.data.length > 1 &&
      description.value &&
      selectedPayrolls.value.length > 0 &&
      selectedWorkingDays.value.length > 0
    )
  })

  const onRowEditSave = (event: any) => {
    let { newData, index } = event
    holidaySteps.data[index] = {
      stepNumber: newData.stepNumber,
      date: newData.date,
      name: newData.name,
    }

    newData = holidaySteps.data[index]

    if (
      (newData.date !== '' || newData.name !== '') &&
      index === holidaySteps.data.length - 1
    ) {
      const newStepNumber = Number(holidaySteps.data[index].stepNumber) + 1
      holidaySteps.data.push({
        stepNumber: newStepNumber,
        date: '',
        name: '',
      })
    }
  }

  const onSave = async () => {
    loadingStore.setLoadingState()
    const isValid =
      (await descValidate()) &&
      (await payrollsValidate()) &&
      (await workingDaysValidate())

    //delete last holiday step if is empty
    holidaySteps.data.pop()

    const createCalendarDto: CreateCalendarGroup = {
      description: description.value,
      onlyPayrolls: selectedPayrolls.value,
      workingDaysIds: selectedWorkingDays.value,
      holidays: holidaySteps.data.map((holiday) => ({
        date: dayjs(holiday.date).format('YYYY-MM-DD'),
        name: holiday.name,
      })),
      payrollConfigId: payrollConfigId.value,
    }

    const response = await createCalendarGroup(createCalendarDto)

    loadingStore.dismissLoadingState()
    if (response === 'OK') {
      notificationStore.addSuccesNotification({
        detail: `200`,
        severity: 'success',
        summary: `Grupo de calendario creado con exito.`,
      })
      router.push(
        '/payrollsConfig/' + payrollConfigId.value + '/calendarGroups'
      )
    }
  }

  const deleteHolidayStep = (event: any, step: Holiday) => {
    holidaySteps.data = holidaySteps.data.filter((ds) => ds.id !== step.id)
  }

  const descriptionSchema = yup.string().required('La descripción es requerida')
  const workingDaysSchema = yup
    .array()
    .min(1, 'Al menos un día hábil debe ser seleccionado')
    .required('Los días hábiles son requeridos')
  const payrollsSchema = yup
    .array()
    .min(1, 'Al menos una nómina debe ser seleccionada')
    .required('Las nóminas son requeridas')
  const {
    value: description,
    errorMessage: descriptionError,
    validate: descValidate,
  } = useField<string>('description', descriptionSchema)
  const {
    value: selectedPayrolls,
    errorMessage: payrollsError,
    validate: payrollsValidate,
  } = useField<[]>('selectedPayrolls', payrollsSchema)
  const {
    value: selectedWorkingDays,
    errorMessage: workingDaysError,
    validate: workingDaysValidate,
  } = useField<[]>('selectedWorkingDays', workingDaysSchema)
  const editingRows = ref([])
</script>
<style scoped>
  :deep(.p-button-label) {
    font-weight: 400;
  }

  :deep(.p-column-title) {
    font-size: 14px;
  }

  :deep(.p-datatable-tbody) {
    font-size: 14px;
  }

  .actions {
    display: flex;
    max-width: 180px;
    flex-direction: row;
  }

  .action-btn {
    height: 20px;
    width: 20px;
    margin-right: 10px;
  }
</style>
